import { useState, useEffect } from "react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

interface CookiePreference {
  id: string;
  name: string;
  description: string;
  required: boolean;
  enabled: boolean;
}

export const CookiePreferences = () => {
  const [preferences, setPreferences] = useState<CookiePreference[]>([
    {
      id: "necessary",
      name: "Necessary Cookies",
      description: "Essential for the website to function properly. These cannot be disabled.",
      required: true,
      enabled: true,
    },
    {
      id: "functional",
      name: "Functional Cookies",
      description: "Enable personalized features and remember your preferences.",
      required: false,
      enabled: true,
    },
    {
      id: "analytics",
      name: "Analytics Cookies",
      description: "Help us understand how visitors interact with our website.",
      required: false,
      enabled: false,
    },
    {
      id: "marketing",
      name: "Marketing Cookies",
      description: "Used to deliver relevant advertisements and track campaign performance.",
      required: false,
      enabled: false,
    },
  ]);

  useEffect(() => {
    const saved = localStorage.getItem("cookiePreferences");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setPreferences((prev) =>
          prev.map((pref) => ({
            ...pref,
            enabled: pref.required ? true : parsed[pref.id] ?? pref.enabled,
          }))
        );
      } catch (e) {
        console.error("Failed to parse cookie preferences", e);
      }
    }
  }, []);

  const handleToggle = (id: string) => {
    setPreferences((prev) =>
      prev.map((pref) =>
        pref.id === id && !pref.required ? { ...pref, enabled: !pref.enabled } : pref
      )
    );
  };

  const handleSave = () => {
    const prefs = preferences.reduce((acc, pref) => {
      acc[pref.id] = pref.enabled;
      return acc;
    }, {} as Record<string, boolean>);

    localStorage.setItem("cookiePreferences", JSON.stringify(prefs));
    toast.success("Cookie preferences saved successfully");
  };

  const handleAcceptAll = () => {
    setPreferences((prev) =>
      prev.map((pref) => ({ ...pref, enabled: true }))
    );
  };

  const handleRejectAll = () => {
    setPreferences((prev) =>
      prev.map((pref) => ({ ...pref, enabled: pref.required }))
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-3">
        <Button onClick={handleAcceptAll} variant="default">
          Accept All
        </Button>
        <Button onClick={handleRejectAll} variant="outline">
          Reject Optional
        </Button>
        <Button onClick={handleSave} variant="secondary">
          Save Preferences
        </Button>
      </div>

      <div className="grid gap-4">
        {preferences.map((pref) => (
          <Card key={pref.id} className="p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <Label htmlFor={pref.id} className="text-base font-semibold cursor-pointer">
                    {pref.name}
                  </Label>
                  {pref.required && (
                    <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                      Required
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">{pref.description}</p>
              </div>
              <Switch
                id={pref.id}
                checked={pref.enabled}
                onCheckedChange={() => handleToggle(pref.id)}
                disabled={pref.required}
              />
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};
